import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { type IntlShape, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { toApiFilter } from 'views/Widgets/Filters/AdvancedSpecializedFilter/model';

import { TGroup } from 'views/Widgets/Filters/types';
import {
  safeDeserializeAdvancedFilter,
  safeSerializeAdvancedFilter,
} from 'views/Widgets/Filters/utils';
import {
  ApiState,
  useConfirmApi,
  useConfirmApiWithDefault,
} from '../../../utils/api/ApiHooks';
import {
  Objective,
  OnFilterChange,
  parseObjectivesForDisplay,
  ViewScope,
} from '../../../utils/models/Objective';
import { getFriendlyUserFacingErrorObjectAndMessage } from '../../../utils/util/util';
import { useObjectivesTimeFrameSelector } from '../ObjectivesTimeFrameSelector';
import { toObjectiveTypeIdsParam } from '../ObjectiveTypeOperations';

type ApiObjectivesOverviewResponse = {
  results: Objective[];
  available_scopes: ViewScope[];
  has_more_entries: boolean;
};

export const RESET_ADVANCED_FILTERS_MARKER = null;

const ADVANCED_FILTER_QUERY_PARAM = 'advanced_filter';

export type ObjectivesOverviewSearchCriteria = {
  scope?: ViewScope;
  status?: string[];
  peopleFilters?: TGroup;
  textFilter?: string;
  tagNames?: string[];
  objectiveTypeIds?: number[];
};

const readInitialCriteria = (
  formatMessage: IntlShape['formatMessage']
): ObjectivesOverviewSearchCriteria => {
  const raw = new URLSearchParams(window.location.search).get(
    ADVANCED_FILTER_QUERY_PARAM
  );
  if (!raw) return {};
  return safeDeserializeAdvancedFilter(raw, formatMessage) ?? {};
};

export const buildObjectivesOverviewSearch = (
  criteria: ObjectivesOverviewSearchCriteria,
  formatMessage: IntlShape['formatMessage']
): string => {
  const serialized = safeSerializeAdvancedFilter(criteria, formatMessage);
  return serialized ? `?${ADVANCED_FILTER_QUERY_PARAM}=${serialized}` : '';
};

export const useObjectivesOverview = ({ availableScopes }) => {
  const { formatMessage } = useIntl();

  const initialCriteriaRef = useRef<ObjectivesOverviewSearchCriteria | null>(
    null
  );
  if (initialCriteriaRef.current === null) {
    initialCriteriaRef.current = readInitialCriteria(formatMessage);
  }
  const initialCriteria = initialCriteriaRef.current;

  const [viewScope, setViewScope] = useState<ViewScope | undefined>(
    initialCriteria.scope
  );
  const [viewStatus, setViewStatus] = useState<string[]>(
    initialCriteria.status ?? []
  );
  const [isLoadingMoreItems, setIsLoadingMoreItems] = useState<boolean>(false);
  const [objectivesToExclude, setObjectivesToExclude] = useState<string[]>([]);
  const [allObjectives, setAllObjectives] = useState<Objective[]>([]);
  const [viewPeopleFilters, setViewPeopleFilters] = useState<
    TGroup | undefined
  >(initialCriteria.peopleFilters);
  const [viewTextFilter, setViewTextFilter] = useState<string>(
    initialCriteria.textFilter ?? ''
  );
  const [viewTagNames, setViewTagNames] = useState<string[]>(
    initialCriteria.tagNames ?? []
  );
  const [viewObjectiveTypeIds, setViewObjectiveTypeIds] = useState<number[]>(
    initialCriteria.objectiveTypeIds ?? []
  );
  const [hasMoreEntries, setHasMoreEntries] = useState<boolean>(false);
  const [status, setStatus] = useState<ApiState>(() => 'LOADING');
  const [isTaskInProgress, setIsTaskInProgress] = useState<boolean>(false);
  const defaultScope: ViewScope | undefined = availableScopes?.[0];
  const loadMoreRef = useRef(null);

  const cleanupData = useCallback(() => {
    setStatus('LOADING');
    setIsLoadingMoreItems(false);
    setAllObjectives([]);
    setObjectivesToExclude([]);
  }, []);

  // Quarter selector
  const {
    currentTimeframeText,
    viewPreviousTimeframe,
    viewNextTimeframe,
    firstDay,
    lastDay,
  } = useObjectivesTimeFrameSelector({});
  const handleViewPreviousTimeframe = useCallback(() => {
    viewPreviousTimeframe();
    cleanupData();
  }, [cleanupData, viewPreviousTimeframe]);

  const handleViewNextTimeframe = useCallback(() => {
    viewNextTimeframe();
    cleanupData();
  }, [cleanupData, viewNextTimeframe]);

  // Other filters (scope, status)
  const handleFilterChange = useCallback(
    ({
      scope,
      status,
      peopleFilters,
      textFilter,
      tagNames,
      objectiveTypeIds,
    }: OnFilterChange) => {
      if (scope) setViewScope(scope);
      if (status) setViewStatus(status);
      // Passing null to distinguish the action of clearing the filter
      if (!!peopleFilters || peopleFilters === RESET_ADVANCED_FILTERS_MARKER)
        setViewPeopleFilters(peopleFilters ?? undefined);
      if (textFilter != null) setViewTextFilter(textFilter);
      if (tagNames) setViewTagNames(tagNames);
      if (objectiveTypeIds) setViewObjectiveTypeIds(objectiveTypeIds);
      cleanupData();
    },
    [cleanupData]
  );

  const handleGenerateCSV = useCallback(() => {
    setStatus('DISABLED');
    setIsTaskInProgress(true);
  }, []);

  const handleGenerateCSVCompleted = useCallback(() => {
    setStatus('SUCCESS');
    setIsTaskInProgress(false);
  }, []);

  // transform data before presenting it to the user
  const postProcess = useCallback((data) => {
    return {
      ...data,
      results: data.results.map((objective) =>
        parseObjectivesForDisplay(objective)
      ),
    };
  }, []);

  // In-page load more handler
  const handleLoadMore = useCallback(() => {
    setIsLoadingMoreItems(true);
    const objectiveToExclude = allObjectives.map((o) => o.key);
    setObjectivesToExclude(objectiveToExclude);
  }, [allObjectives]);

  // Infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const target = entries[0];
      if (target.isIntersecting && hasMoreEntries) {
        handleLoadMore();
      }
    });
    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
      return () => {
        observer.unobserve(currentRef);
      };
    }
  }, [handleLoadMore, hasMoreEntries]);

  useEffect(() => {
    if (isLoadingMoreItems && status === 'ERROR') {
      toast.error('Error loading more objectives');
      setIsLoadingMoreItems(false);
      setStatus('SUCCESS');
    }
  }, [isLoadingMoreItems, status]);

  useEffect(() => {
    if (defaultScope && !viewScope) {
      setViewScope(defaultScope);
    }
  }, [defaultScope, viewScope]);

  // Sync all search criteria into a single bookmarkable URL param.
  useEffect(() => {
    const criteria: ObjectivesOverviewSearchCriteria = {};
    if (viewScope && viewScope !== defaultScope) criteria.scope = viewScope;
    if (viewStatus.length > 0) criteria.status = viewStatus;
    if (viewPeopleFilters) criteria.peopleFilters = viewPeopleFilters;
    if (viewTextFilter) criteria.textFilter = viewTextFilter;
    if (viewTagNames.length > 0) criteria.tagNames = viewTagNames;
    if (viewObjectiveTypeIds.length > 0)
      criteria.objectiveTypeIds = viewObjectiveTypeIds;

    const params = new URLSearchParams(window.location.search);
    if (Object.keys(criteria).length === 0) {
      params.delete(ADVANCED_FILTER_QUERY_PARAM);
    } else {
      const serialized = safeSerializeAdvancedFilter(criteria, formatMessage);
      if (serialized) {
        params.set(ADVANCED_FILTER_QUERY_PARAM, serialized);
      }
    }
    const queryString = params.toString();
    const nextUrl = queryString
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname;
    if (nextUrl !== `${window.location.pathname}${window.location.search}`) {
      window.history.replaceState(null, '', nextUrl);
    }
  }, [
    viewScope,
    viewStatus,
    viewPeopleFilters,
    viewTextFilter,
    viewTagNames,
    viewObjectiveTypeIds,
    defaultScope,
    formatMessage,
  ]);

  //   Api call
  const callback = useCallback(
    (data, error: unknown, hardErrorMessage: unknown) => {
      if (error || hardErrorMessage) {
        const [errorObject] = getFriendlyUserFacingErrorObjectAndMessage(
          error,
          hardErrorMessage
        );
        console.log(JSON.stringify(errorObject));
        setStatus('ERROR');
        return;
      }
      if (data) {
        setAllObjectives((objectives) => [...objectives, ...data.results]);
        setIsLoadingMoreItems(false);
        setHasMoreEntries(data.has_more_entries);
      }
      setStatus('SUCCESS');
    },
    []
  );

  const markMatchingObjectives =
    viewStatus.length > 0 ||
    viewPeopleFilters ||
    viewTextFilter.length > 0 ||
    viewTagNames.length > 0;

  const currentScope = viewScope || defaultScope;

  useConfirmApi<ApiObjectivesOverviewResponse>({
    method: 'POST',
    url: '/objectives/overview',
    params: {
      coverage_start_date: firstDay,
      coverage_end_date: lastDay,
      scope: currentScope,
      status: viewStatus,
      excluded_objective_keys: objectivesToExclude,
      people_filters: toApiFilter(viewPeopleFilters),
      text_filter: viewTextFilter,
      tag_names: viewTagNames,
      mark_matching_objectives: markMatchingObjectives,
      objective_type_ids: toObjectiveTypeIdsParam(viewObjectiveTypeIds),
    },
    postProcess,
    callback,
    disabled: !defaultScope, // wait until the default scope is set
  });

  return {
    handleViewNextTimeframe,
    currentTimeframeText,
    handleViewPreviousTimeframe,
    isLoadingMoreItems,
    handleFilterChange,
    viewStatus,
    viewScope,
    viewPeopleFilters,
    viewTextFilter,
    viewTagNames,
    viewObjectiveTypeIds,
    availableScopes,
    allObjectives,
    loadMoreRef,
    handleGenerateCSV,
    handleGenerateCSVCompleted,
    isTaskInProgress,
    status,
    firstDay,
    lastDay,
  };
};

type AvailableScopesGetResponse = {
  available_scopes: ViewScope[];
};

export const useAvailableScopes = (): ViewScope[] => {
  const {
    data: { available_scopes: availableScopes },
  } = useConfirmApiWithDefault<AvailableScopesGetResponse>({
    method: 'GET',
    url: '/objectives/overview/scopes',
    defaultValue: { available_scopes: [] },
  });

  return availableScopes;
};

export const useInfiniteScroll = (
  loadMoreRef: MutableRefObject<null>,
  onLoadMore: () => void
): void => {
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const target = entries[0];
      if (target.isIntersecting) {
        onLoadMore();
      }
    });
    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
      return () => {
        observer.unobserve(currentRef);
      };
    }
  }, [loadMoreRef, onLoadMore]);
};
